import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Cancel, CheckCircle, DoneAll } from '@mui/icons-material'
import { Box, Button, Card, Stack, TableContainer, TextField, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn, SortOrder } from '../../../../../components/GRTable/GRTable'
import GameSubgenrePicker from '../../../../../components/GameSubgenrePicker/GameSubgenrePicker'
import { SubgenreMap } from '../../../../../features/account'
import { GameIcon } from '../../../../../features/game'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import { useGetTrackedGamesQuery } from '../../../../api/internal'
import { QueryStateBoundary } from '../../../../components/QueryStateBoundary/QueryStateBoundary'
import { InternalOrganizationPermissions, InternalPermissionType } from '../../../../types/InternalPermission'
import { useOrganizationAccessChange } from '../../hooks/useOrganizationAccessChange'
import { AccessStatus } from '../AccessStatus/AccessStatus'
import { AccessToggleButton } from '../AccessToggleButton/AccessToggleButton'
import { filterTrackedGames, getGrantedIds, getTrackedGameSubgenre } from '../helpers/organizationAccessTabHelpers'

type GameRow = {
  id: string
  name: string
  icon?: string
  publisher: string
  subgenre: string
  published: boolean
  hasAccess: boolean
}

type CustomProps = {
  pendingIds: ReadonlySet<string>
  isGrantingAll: boolean
  onToggle: (row: GameRow, granted: boolean) => void
}

type OrganizationLiveEventGamesAccessProps = {
  organization: { id: string; name: string }
  permissions: InternalOrganizationPermissions
}

export const OrganizationLiveEventGamesAccess: FC<OrganizationLiveEventGamesAccessProps> = ({ organization, permissions }) => {
  const { t } = useTranslation()
  const trackedGamesQuery = useGetTrackedGamesQuery()
  const { pendingIds, changeAccess, grantAllListed, isGrantingAll, isBusy } = useOrganizationAccessChange(organization, InternalPermissionType.LiveEvents)
  const [subgenres, setSubgenres] = useState<SubgenreMap>({})
  const [search, setSearch] = useState('')
  const [grantAllConfirm, setGrantAllConfirm] = useState<ConfirmDialogData<string[]>>()

  const granted = useMemo(() => getGrantedIds(permissions, InternalPermissionType.LiveEvents), [permissions])
  const rows = useMemo<GameRow[]>(
    () =>
      filterTrackedGames(trackedGamesQuery.data ?? [], subgenres, search).map((trackedGame) => ({
        id: trackedGame.game.id,
        name: trackedGame.game.name ?? '',
        icon: trackedGame.game.getIcon?.(false) as string | undefined,
        publisher: trackedGame.game.artist ?? '',
        subgenre: getTrackedGameSubgenre(trackedGame),
        published: trackedGame.published,
        hasAccess: granted.has(trackedGame.game.id),
      })),
    [granted, search, subgenres, trackedGamesQuery.data]
  )
  const grantedCount = rows.filter(({ hasAccess }) => hasAccess).length

  const tableColumns: GRTableColumn<GameRow, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-csm:organization_access_icon'),
        accessor: ({ row }) => (row.icon ? <GameIcon src={row.icon} gameName={row.name} size="small" /> : null),
        headerCellProps: { sx: { width: 72, minWidth: 72 } },
      },
      {
        labelAccessor: t('internal-csm:organization_access_game'),
        accessor: ({ row }) => (
          <Typography fontWeight="bold" sx={{ overflowWrap: 'anywhere' }}>
            {row.name || '---'}
          </Typography>
        ),
        sortable: true,
        sortAccessor: ({ row }) => row.name.toLowerCase(),
        sortOrder: SortOrder.DESC,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 200 } },
      },
      {
        labelAccessor: t('internal-csm:organization_access_publisher'),
        accessor: ({ row }) => row.publisher || '---',
        sortable: true,
        sortAccessor: ({ row }) => row.publisher.toLowerCase(),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 140 } },
      },
      {
        labelAccessor: t('internal-csm:organization_access_subgenre'),
        accessor: ({ row }) => row.subgenre || '---',
        sortable: true,
        sortAccessor: ({ row }) => row.subgenre.toLowerCase(),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 140 } },
      },
      {
        labelAccessor: t('internal-csm:organization_access_published'),
        accessor: ({ row }) =>
          row.published ? (
            <CheckCircle color="success" titleAccess={t('internal-csm:organization_access_published_yes')} />
          ) : (
            <Cancel color="error" titleAccess={t('internal-csm:organization_access_published_no')} />
          ),
        sortable: true,
        sortAccessor: ({ row }) => (row.published ? 1 : 0),
        headerCellProps: { sx: { minWidth: 90 } },
      },
      {
        labelAccessor: t('internal-csm:organization_access_access'),
        accessor: ({ row }) => <AccessStatus granted={row.hasAccess} />,
        sortable: true,
        sortAccessor: ({ row }) => (row.hasAccess ? 1 : 0),
        headerCellProps: { sx: { minWidth: 100 } },
      },
      {
        labelAccessor: '',
        accessor: ({ row, customTableProps }) => (
          <AccessToggleButton
            granted={row.hasAccess}
            pending={!!customTableProps?.isGrantingAll || !!customTableProps?.pendingIds.has(row.id)}
            itemName={row.name || row.id}
            onToggle={() => customTableProps?.onToggle(row, !row.hasAccess)}
          />
        ),
        cellProps: { align: 'right' },
        headerCellProps: { sx: { minWidth: 150 } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({ pendingIds, isGrantingAll, onToggle: (row, grant) => changeAccess({ id: row.id, name: row.name || row.id }, grant) }),
    [changeAccess, isGrantingAll, pendingIds]
  )

  const openGrantAll = () =>
    setGrantAllConfirm({
      title: t('internal-csm:organization_access_grant_all'),
      confirmText: t('internal-csm:organization_access_grant_all_confirm', { count: rows.length, organization: organization.name }),
      actionText: t('internal-csm:organization_access_grant_all_action'),
      data: rows.map(({ id }) => id),
    })

  const handleGrantAllConfirm = (confirmed?: ConfirmDialogData<string[]>) => {
    setGrantAllConfirm(undefined)
    if (confirmed?.data) grantAllListed(confirmed.data)
  }

  return (
    <Box>
      <Box mb={2}>
        <GameSubgenrePicker hideSetAsDefault selectedSubgenres={subgenres} selectionChanged={setSubgenres} />
      </Box>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
        <TextField
          size="small"
          type="search"
          label={t('internal-csm:organization_access_search_games')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 280 }}
        />
        {trackedGamesQuery.data && (
          <Typography variant="body2" color="text.secondary">
            {t('internal-csm:organization_access_games_count', { granted: grantedCount, count: rows.length })}
          </Typography>
        )}
        <Box flexGrow={1} />
        <Button
          variant="contained"
          startIcon={<DoneAll />}
          onClick={openGrantAll}
          disabled={!trackedGamesQuery.data || isBusy || rows.length === 0 || grantedCount === rows.length}
        >
          {t('internal-csm:organization_access_grant_all_count', { count: rows.length })}
        </Button>
      </Stack>
      <QueryStateBoundary
        isLoading={trackedGamesQuery.isLoading}
        isError={trackedGamesQuery.isError}
        errorText={t('internal-csm:organization_access_games_failed')}
        skeletonHeight={480}
      >
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey="id"
            customProps={customProps}
            noRowsLabel={t('internal-csm:organization_access_no_games')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>
      <ConfirmDialog open={!!grantAllConfirm} confirmDialogData={grantAllConfirm} onClose={handleGrantAllConfirm} />
    </Box>
  )
}
